import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * Button hierarchy — four steps, and each one has to be obviously quieter than
 * the last: solid accent → tinted outline → bare outline → ghost.
 *
 * The primary fill is a shallow top-to-bottom gradient of the accent rather
 * than a flat slab. It is the same trick a physical button plays: the lit face
 * is brighter at the top, and a one-pixel brighter top border finishes the
 * bevel. Both are built with `color-mix` on `--primary`, so a tenant that sets
 * its own brand colour gets the identical treatment for free — nothing here
 * assumes ember.
 */
const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          "border border-transparent border-t-[color:color-mix(in_oklab,var(--primary)_70%,white)] bg-primary bg-[image:linear-gradient(to_bottom,color-mix(in_oklab,var(--primary)_88%,white),var(--primary))] text-primary-foreground shadow-[var(--elev-1)] hover:bg-[image:linear-gradient(to_bottom,var(--primary),color-mix(in_oklab,var(--primary)_90%,black))] active:shadow-none",
        /**
         * The primary ACTION — Save, Subscribe, Create. Yellow, and deliberately
         * a different colour from `default`: the blue is the system (links,
         * active states, focus rings), so if the confirm button were also blue
         * it would be one more blue thing on a page full of them. One yellow
         * control per screen, or nothing is primary.
         */
        action:
          "border border-transparent border-t-[color:color-mix(in_oklab,var(--action)_70%,white)] bg-[var(--action)] bg-[image:linear-gradient(to_bottom,color-mix(in_oklab,var(--action)_90%,white),var(--action))] text-[var(--action-foreground)] shadow-[var(--elev-1)] hover:bg-[image:linear-gradient(to_bottom,var(--action),color-mix(in_oklab,var(--action)_90%,black))] active:shadow-none",
        destructive:
          "bg-destructive text-white shadow-[var(--elev-1)] hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/70 dark:focus-visible:ring-destructive/40",
        outline:
          "border bg-card shadow-[var(--elev-1)] hover:border-border hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-input/20 dark:hover:bg-input/40",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/70",
        ghost:
          "hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/60",
        link: "text-brand-ink underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
